import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import Modal from './Modal';
import { useLanguage } from '../context/LanguageContext';

function AddExpenseModal({ 
  isOpen, 
  onClose, 
  onSubmit, 
  expenseItems = [],
  initialData = {
    category: '',
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0]
  }
}) {
  const { t } = useLanguage();
  const [formData, setFormData] = useState(initialData);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    // Reset form
    setFormData({
      category: '',
      description: '',
      amount: '',
      date: new Date().toISOString().split('T')[0]
    });
  };

  const handleClose = () => {
    // Reset form when closing
    setFormData({
      category: '',
      description: '',
      amount: '',
      date: new Date().toISOString().split('T')[0]
    });
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('Add New Expense')}
      size="lg"
    >
      <div className="p-4 sm:p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Expense Details Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
              {t('Expense Details')}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Category Field */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('Category')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 sm:px-4 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:border-gray-400 appearance-none bg-white pr-10"
                    required
                  >
                    <option value="">{t('Select an expense category')}</option>
                    {expenseItems.map((item) => (
                      <option key={item._id} value={item.name}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {t('Choose the category for this expense')}
                </p>
              </div>

              {/* Description Field */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('Description')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('e.g., Office supplies for marketing team, Monthly electricity bill')}
                  className="w-full px-3 sm:px-4 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:border-gray-400"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('Enter a detailed description of the expense')}
                </p>
              </div>

              {/* Amount Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('Amount (Rs.)')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-base">Rs</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0"
                    min="0"
                    step="1"
                    className="w-full pl-8 pr-3 sm:pr-4 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:border-gray-400"
                    required
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">{t('Enter the total expense amount')}</p>
              </div>

              {/* Date Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('Date')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-3 sm:px-4 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:border-gray-400"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">{t('When did this expense occur?')}</p>
              </div>
            </div>

            {/* Quick Amount Buttons */}
            <div className="bg-gray-50 p-4 rounded-xl">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                {t('Quick Amount Selection')}
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {[100, 250, 500, 1000, 2500, 5000].map((amount) => (
                  <button
                    key={amount}
                    type="button"
                    onClick={() => setFormData({ ...formData, amount: amount.toString() })}
                    className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors text-center"
                  >
                    Rs {amount}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">{t('Click to quickly set common amounts')}</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
            <button 
              type="submit" 
              className="flex-1 sm:flex-none sm:min-w-[140px] px-6 py-3.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 font-medium transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <Plus className="h-5 w-5" />
              <span>{t('Add Expense')}</span>
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 sm:flex-none sm:min-w-[100px] px-6 py-3.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 font-medium transition-all duration-200 border border-gray-200 hover:border-gray-300"
            >
              {t('Cancel')}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default AddExpenseModal;